import {
  Button,
  Card,
  Input,
  InputNumber,
  Popconfirm,
  Radio,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  notification,
} from "antd";
import {
  CheckOutlined,
  ReloadOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { useContext, useEffect, useState } from "react";

import { Context } from "../../../context";
import { Fetch, Post, Put } from "../../../fetch";
import { useBank } from "./BankContext";

const { Column } = Table;
const { Text } = Typography;

const TYPES = ["translation", "fill_blank", "synonym", "antonym", "grammar"];
const STATUS_COLOR = { draft: "warning", approved: "success", retired: "default" };
const OPTION_LABELS = ["A", "B", "C", "D"];
const GRADES = [1, 2, 3, 4, 5];
const GRADE_COLOR = { 1: "green", 2: "cyan", 3: "blue", 4: "purple", 5: "magenta" };

/** Inline editor for one question row. */
export const QuestionEditor = ({ question, onSaved, apiUrl, token }) => {
  const bankInfo = useBank();
  const [q, setQ] = useState(question);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    Fetch(`${apiUrl}/v4/quiz-config/?configKey=${bankInfo.configKey}`, token, (result) => {
      const rows = (result && result.config && result.config.categories) || [];
      setCategories(rows.filter((row) => row && row.id));
    });
  }, [apiUrl, token, bankInfo.configKey]);

  const set = (patch) => setQ((prev) => ({ ...prev, ...patch }));
  const setOption = (index, value) =>
    setQ((prev) => ({
      ...prev,
      options: prev.options.map((o, i) => (i === index ? value : o)),
    }));

  const save = (status) => {
    setSaving(true);
    const body = {
      level: q.level,
      topic: q.topic,
      type: q.type,
      question_hi: q.question_hi,
      question_en: q.question_en || null,
      options: q.options,
      correct_index: q.correct_index,
      explanation_hi: q.explanation_hi || null,
      ...(q.difficulty_grade != null ? { difficulty_grade: q.difficulty_grade } : {}),
      ...(q.category_id ? { category_id: q.category_id, level_id: q.level_id || null } : {}),
    };
    if (status) body.status = status;
    Put(
      `${apiUrl}/v4/quiz-config/questions/${q._id}`,
      token,
      body,
      (result) => {
        setSaving(false);
        if (result && result.question) {
          notification.success({ message: status ? `Question ${status}` : "Question saved" });
          onSaved(result.question);
        } else {
          notification.error({
            message:
              (result && typeof result.detail === "string" && result.detail) ||
              "Save failed",
          });
        }
      },
      () => {
        setSaving(false);
        notification.error({ message: "Save failed" });
      }
    );
  };

  return (
    <Space direction="vertical" style={{ width: "100%", padding: "8px 0" }}>
      <Space size="large" wrap>
        <Space direction="vertical" size={2}>
          <Text>Level</Text>
          <InputNumber min={1} value={q.level} onChange={(v) => set({ level: v || 1 })} />
        </Space>
        <Space direction="vertical" size={2}>
          <Text>Type</Text>
          <Select
            style={{ width: 160 }}
            value={q.type}
            onChange={(v) => set({ type: v })}
            options={bankInfo.types.map((t) => ({ value: t, label: t }))}
          />
        </Space>
        <Space direction="vertical" size={2}>
          <Text>Category</Text>
          <Select
            allowClear
            style={{ width: 220 }}
            placeholder="Assign category"
            value={q.category_id || undefined}
            options={categories.map((row) => ({ value: row.id, label: row.title || row.id }))}
            onChange={(value) => {
              const row = categories.find((item) => item.id === value);
              const first = ((row && row.levels) || []).find((lv) => lv.enabled !== false);
              set({
                category_id: value || null,
                level_id: first ? first.id : null,
                topic: (row && row.topic) || q.topic,
                level: (first && first.level) || q.level,
              });
            }}
          />
        </Space>
        <Space direction="vertical" size={2}>
          <Text>Category level</Text>
          <Select
            allowClear
            style={{ width: 160 }}
            disabled={!q.category_id}
            value={q.level_id || undefined}
            options={((categories.find((row) => row.id === q.category_id) || {}).levels || [])
              .filter((lv) => lv.enabled !== false)
              .map((lv) => ({ value: lv.id, label: lv.title || lv.id }))}
            onChange={(value) => {
              const row = categories.find((item) => item.id === q.category_id);
              const lv = ((row && row.levels) || []).find((item) => item.id === value);
              set({
                level_id: value || null,
                level: (lv && lv.level) || q.level,
              });
            }}
          />
        </Space>
        <Space direction="vertical" size={2}>
          <Text>Topic</Text>
          <Input
            style={{ width: 260 }}
            value={q.topic || ""}
            onChange={(e) => set({ topic: e.target.value })}
          />
        </Space>
        <Space direction="vertical" size={2}>
          <Text>Grade (1-5)</Text>
          <Select
            style={{ width: 100 }}
            value={q.difficulty_grade ?? null}
            onChange={(v) => set({ difficulty_grade: v })}
            options={GRADES.map((g) => ({ value: g, label: `G${g}` }))}
          />
        </Space>
      </Space>
      <Space direction="vertical" size={2} style={{ width: "100%" }}>
        <Text>Question (Hindi)</Text>
        <Input.TextArea
          rows={2}
          value={q.question_hi}
          onChange={(e) => set({ question_hi: e.target.value })}
        />
      </Space>
      <Space direction="vertical" size={2} style={{ width: "100%" }}>
        <Text>Question (English, optional)</Text>
        <Input
          value={q.question_en || ""}
          onChange={(e) => set({ question_en: e.target.value })}
        />
      </Space>
      <Space direction="vertical" size={2} style={{ width: "100%" }}>
        <Text>Options — pick the correct one</Text>
        <Radio.Group
          value={q.correct_index}
          onChange={(e) => set({ correct_index: e.target.value })}
          style={{ width: "100%" }}
        >
          <Space direction="vertical" style={{ width: "100%" }}>
            {(q.options || []).map((option, i) => (
              <Space key={OPTION_LABELS[i]} style={{ width: "100%" }}>
                <Radio value={i}>{OPTION_LABELS[i]}</Radio>
                <Input
                  style={{ width: 420 }}
                  value={option}
                  onChange={(e) => setOption(i, e.target.value)}
                />
              </Space>
            ))}
          </Space>
        </Radio.Group>
      </Space>
      <Space direction="vertical" size={2} style={{ width: "100%" }}>
        <Text>Explanation (Hindi)</Text>
        <Input.TextArea
          rows={2}
          value={q.explanation_hi || ""}
          onChange={(e) => set({ explanation_hi: e.target.value })}
        />
      </Space>
      <Space>
        <Button loading={saving} onClick={() => save(null)}>
          Save edits
        </Button>
        {q.status !== "approved" && (
          <Button
            type="primary"
            icon={<CheckOutlined />}
            loading={saving}
            onClick={() => save("approved")}
          >
            Save & approve
          </Button>
        )}
        {q.status !== "retired" && (
          <Popconfirm title="Retire this question?" onConfirm={() => save("retired")}>
            <Button danger icon={<StopOutlined />} loading={saving}>
              Retire
            </Button>
          </Popconfirm>
        )}
      </Space>
    </Space>
  );
};

const ReviewTab = () => {
  const { token, apiUrl } = useContext(Context);
  const bankInfo = useBank();

  const [questions, setQuestions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [level, setLevel] = useState(null);
  const [status, setStatus] = useState("draft");
  const [type, setType] = useState(null);
  const [gradeFilter, setGradeFilter] = useState(null);
  const [regrade, setRegrade] = useState(null);
  const [selected, setSelected] = useState([]);
  const [counter, setCounter] = useState(0);

  const SIZE = 20;

  useEffect(() => {
    const params = new URLSearchParams({ page: String(page), size: String(SIZE) });
    if (level != null) params.set("level", String(level));
    if (status) params.set("status", status);
    if (type) params.set("type", type);
    if (gradeFilter != null) params.set("difficulty_grade", String(gradeFilter));
    params.set("bank", bankInfo.bank);
    Fetch(`${apiUrl}/v4/quiz-config/questions?${params}`, token, (result) => {
      if (result && result.questions) {
        setQuestions(result.questions);
        setTotal(result.total || 0);
        setSelected([]);
      }
    });
  }, [apiUrl, token, page, level, status, type, gradeFilter, counter, bankInfo.bank]);

  const replaceQuestion = (updated) =>
    setQuestions((rows) => rows.map((r) => (r._id === updated._id ? updated : r)));

  const bulk = (newStatus) => {
    Post(
      `${apiUrl}/v4/quiz-config/questions/bulk-status`,
      token,
      { ids: selected, status: newStatus },
      (result) => {
        if (result && result.success) {
          notification.success({ message: `${result.modified} question(s) ${newStatus}` });
          setCounter((n) => n + 1);
        } else {
          notification.error({ message: "Bulk update failed" });
        }
      },
      () => notification.error({ message: "Bulk update failed" })
    );
  };

  const bulkRegrade = () => {
    if (regrade == null) return;
    Post(
      `${apiUrl}/v4/quiz-config/questions/bulk-grade`,
      token,
      { ids: selected, difficulty_grade: regrade },
      (result) => {
        if (result && result.success) {
          notification.success({
            message: `${result.modified} question(s) regraded to G${regrade}`,
          });
          setCounter((n) => n + 1);
        } else {
          notification.error({ message: "Bulk regrade failed" });
        }
      },
      () => notification.error({ message: "Bulk regrade failed" })
    );
  };

  return (
    <Card
      title={`Questions (${total})`}
      size="small"
      extra={
        <Space>
          <InputNumber
            placeholder="Level"
            min={1}
            value={level}
            onChange={setLevel}
            style={{ width: 90 }}
          />
          <Select
            style={{ width: 130 }}
            value={status}
            onChange={(v) => {
              setStatus(v);
              setPage(1);
            }}
            options={[
              { value: "draft", label: "Draft" },
              { value: "approved", label: "Approved" },
              { value: "retired", label: "Retired" },
              { value: "", label: "All statuses" },
            ]}
          />
          <Select
            allowClear
            placeholder="Type"
            style={{ width: 140 }}
            value={type}
            onChange={setType}
            options={bankInfo.types.map((t) => ({ value: t, label: t }))}
          />
          <Select
            allowClear
            placeholder="Grade"
            style={{ width: 100 }}
            value={gradeFilter}
            onChange={(v) => {
              setGradeFilter(v ?? null);
              setPage(1);
            }}
            options={GRADES.map((g) => ({ value: g, label: `G${g}` }))}
          />
          <Button icon={<ReloadOutlined />} onClick={() => setCounter((n) => n + 1)} />
        </Space>
      }
    >
      <Space style={{ marginBottom: 12 }}>
        <Button
          type="primary"
          icon={<CheckOutlined />}
          disabled={!selected.length}
          onClick={() => bulk("approved")}
        >
          Approve selected ({selected.length})
        </Button>
        <Popconfirm
          title={`Retire ${selected.length} question(s)?`}
          onConfirm={() => bulk("retired")}
          disabled={!selected.length}
        >
          <Button danger icon={<StopOutlined />} disabled={!selected.length}>
            Retire selected
          </Button>
        </Popconfirm>
        <Select
          placeholder="Grade…"
          style={{ width: 100 }}
          value={regrade}
          onChange={setRegrade}
          options={GRADES.map((g) => ({ value: g, label: `G${g}` }))}
        />
        <Button disabled={!selected.length || regrade == null} onClick={bulkRegrade}>
          Regrade selected ({selected.length})
        </Button>
      </Space>
      <Table
        dataSource={questions.map((q) => ({ ...q, key: q._id }))}
        size="small"
        rowSelection={{
          selectedRowKeys: selected,
          onChange: setSelected,
        }}
        pagination={{
          current: page,
          pageSize: SIZE,
          total,
          onChange: setPage,
          showSizeChanger: false,
        }}
        expandable={{
          expandedRowRender: (r) => (
            <QuestionEditor
              question={r}
              onSaved={replaceQuestion}
              apiUrl={apiUrl}
              token={token}
            />
          ),
        }}
      >
        <Column title="Level" dataIndex="level" width={65} />
        <Column
          title="Grade"
          width={75}
          render={(_, r) =>
            r.difficulty_grade != null ? (
              <Tag color={GRADE_COLOR[r.difficulty_grade]}>
                G{r.difficulty_grade}
              </Tag>
            ) : (
              <Tag>—</Tag>
            )
          }
        />
        <Column
          title="Type"
          dataIndex="type"
          width={110}
          render={(v) => <Tag>{v}</Tag>}
        />
        <Column title="Question (Hindi)" dataIndex="question_hi" ellipsis />
        <Column
          title="Correct"
          width={220}
          render={(_, r) => (r.options || [])[r.correct_index]}
        />
        <Column
          title="Status"
          width={100}
          render={(_, r) => (
            <Tag color={STATUS_COLOR[r.status] || "default"}>{r.status}</Tag>
          )}
        />
        <Column
          title="Source"
          dataIndex="source"
          width={90}
        />
        <Column
          title="Served / OK"
          width={110}
          render={(_, r) =>
            r.stats ? `${r.stats.served || 0} / ${r.stats.correct || 0}` : "-"
          }
        />
      </Table>
    </Card>
  );
};

export default ReviewTab;
